/**
 * Explicit "Refresh now" runs (S6). Unlike the scheduler's refetches (which
 * run under `interval` policy — read-only unless trusted), a manual run uses
 * `trigger: 'manual'`: destructive SQL goes through the confirm flow the
 * dialog host wires via `setTileDangerousConfirmer`. The result lands in the
 * SAME TanStack cache entry `useTileResult` reads, and the runtime store
 * settles exactly like any other run.
 */
import { getSharedQueryClient } from '@/core/query/sharedQueryClient';
import { getEngineForConnection } from '@/features/connections/utils/connectionLookup';
import type { DashboardTile, NoteTile } from '../types/dashboardTypes';
import type { TileRunResult } from '../types/dashboardRuntime';
import { startTileExecution, type TileParamContext } from './tileExecutionService';
import {
  refetchCurrentDashboardTile,
  selectDashboardTileQuery,
  isSelectedDashboardTileQuery,
  dashboardTileQueryKey,
  stableValueKey,
} from './dashboardTileCache';
import { tileRunInputsHash } from './paramHashing';
import { relevantFiltersForTile } from './crossFilter';
import { useDashboardParamStore } from '../store/dashboardParamStore';
import { useDashboardRuntimeStore } from '../store/dashboardRuntimeStore';
import { useDashboardStore } from '../store/dashboardStore';
import {
  selectActiveFilters,
  useDashboardCrossFilterStore,
} from '../store/dashboardCrossFilterStore';

/** Same derivation inputs `useTileResult` renders with — a manual run must
 * land in the SAME param-keyed cache entry the tile view is reading. */
const currentParamContext = (dashboardId: string, connectionId: string): TileParamContext => ({
  parameters:
    useDashboardStore.getState().dashboards.find((d) => d.id === dashboardId)?.parameters ?? [],
  values: useDashboardParamStore.getState().valuesByDashboard[dashboardId] ?? {},
  engine: getEngineForConnection(connectionId),
});

export async function runTileManually(
  dashboardId: string,
  tile: Exclude<DashboardTile, NoteTile>,
): Promise<TileRunResult> {
  const store = useDashboardRuntimeStore.getState();
  const paramContext = currentParamContext(dashboardId, tile.connectionId);
  // A manual run must reproduce the tile's VISIBLE state — the same active
  // cross-filters `useTileResult` renders with. Passing `[]` ran the query
  // UNFILTERED and wrote the unfiltered cache key, so a filtered tile's shown
  // (filtered) entry was never refreshed and the query returned wrong rows.
  const activeFilters = selectActiveFilters(dashboardId)(useDashboardCrossFilterStore.getState());
  const filters = relevantFiltersForTile(tile, activeFilters, paramContext.engine);
  const runInputsHash = tileRunInputsHash({ tile, ...paramContext });
  const key = dashboardTileQueryKey(dashboardId, tile.id, runInputsHash, stableValueKey(filters));
  const client = getSharedQueryClient();
  if (client) selectDashboardTileQuery(client, key);
  const { executionId, promise } = startTileExecution(tile, paramContext, filters, {
    trigger: 'manual',
    dashboardId,
  });
  store.begin(dashboardId, tile.id, executionId);
  const result = await promise;
  // The runtime store is the ONE arbiter of "newest run wins": a result it
  // discards (a newer manual or scheduler run began meanwhile) must not
  // reach the cache either — written unconditionally, an older run landing
  // late overwrote the fresher rows the tile was already showing.
  const applied = store.settle(dashboardId, tile.id, executionId, result);
  if (applied && result.ok && client && isSelectedDashboardTileQuery(client, key)) {
    client.setQueryData(key, result);
  }
  return result;
}

/**
 * Re-runs a tile whose cache key did NOT change but whose run policy did
 * (D2 schedule-trust grant). Data-input edits need no call here — they are
 * in the key. Refetching (not just invalidating) because dashboard tiles
 * have no user-visible "stale" affordance — the tile should show fresh data
 * now.
 */
export function refetchTileQueries(dashboardId: string, tileId: string): void {
  const client = getSharedQueryClient();
  if (client) void refetchCurrentDashboardTile(client, dashboardId, tileId);
}
